import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import "@/i18n";
import { lazy, Suspense } from "react";
import RedirectHandler from "./components/RedirectHandler";
import ScrollToTop from "./components/ScrollToTop";
import TrackingScripts from "./components/TrackingScripts";
import TrackingInit from "./components/TrackingInit";
import PageViewTracker from "./components/PageViewTracker";
import CookieConsent from "./components/CookieConsent";
import LanguageRouter from "./components/LanguageRouter";
import { PageAlternatesProvider } from "@/contexts/PageAlternatesContext";
import config from "@/i18n/config";
import { getLocalizedSlug } from "@/i18n/detectLanguage";

// Index (homepage) is eager — it's the most common entry point and the main
// LCP target. Everything else is code-split so the initial bundle only pulls
// what the homepage needs.
import Index from "./pages/Index";

// Marketing pages (lazy)
const Memberships = lazy(() => import("./pages/Memberships"));
const MembershipDetail = lazy(() => import("./pages/MembershipDetail"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQs = lazy(() => import("./pages/FAQs"));
const News = lazy(() => import("./pages/News"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Privileges = lazy(() => import("./pages/Privileges"));
const Visa = lazy(() => import("./pages/Visa"));
const ThailandPrivilegeCard = lazy(() => import("./pages/ThailandPrivilegeCard"));
const PrivilegePoints = lazy(() => import("./pages/PrivilegePoints"));
const HowToApply = lazy(() => import("./pages/HowToApply"));
const Apply = lazy(() => import("./pages/Apply"));
const ApplyLegacy = lazy(() => import("./pages/ApplyLegacy"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Family = lazy(() => import("./pages/Family"));
const Compare = lazy(() => import("./pages/Compare"));
const Guides = lazy(() => import("./pages/Guides"));
const GuideDetail = lazy(() => import("./pages/GuideDetail"));
const Downloads = lazy(() => import("./pages/Downloads"));
const DiscontinuedMemberships = lazy(() => import("./pages/DiscontinuedMemberships"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Portal pages (lazy — only authenticated users hit these)
const Login = lazy(() => import("./pages/portal/Login"));
const AuthCallback = lazy(() => import("./pages/portal/AuthCallback"));
const Register = lazy(() => import("./pages/portal/Register"));
const ForgotPassword = lazy(() => import("./pages/portal/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const PortalDashboard = lazy(() => import("./pages/portal/Dashboard"));
const OnboardingSurvey = lazy(() => import("./pages/portal/OnboardingSurvey"));
const MemberLinkConfirm = lazy(() => import("./pages/portal/MemberLinkConfirm"));
const Feedback = lazy(() => import("./pages/Feedback"));
const ApplicationWizard = lazy(() => import("./components/portal/ApplicationWizard"));

// Partner portal (lazy — external partners only, no SEO value)
const PartnerOnboarding = lazy(() => import("./pages/partner/PartnerOnboarding"));
const PartnerPending = lazy(() => import("./pages/partner/PartnerPending"));
const PartnerDashboard = lazy(() => import("./pages/partner/PartnerDashboard"));
const PartnerRegister = lazy(() => import("./pages/partner/PartnerRegister"));

// Portal/admin guards are eager (small, used by many routes)
import ProtectedRoute from "./components/portal/ProtectedRoute";
import PartnerRoute from "./components/partner/PartnerRoute";
import AdminRoute from "./components/admin/AdminRoute";
import AdminLayout from "./components/admin/AdminLayout";

// Admin pages (lazy — staff-only, no SEO value)
const AdminPreview = lazy(() => import("./pages/admin/AdminPreview"));
const ApplicationsPage = lazy(() => import("./pages/admin/sections/ApplicationsPage"));
const PipelinePage = lazy(() => import("./pages/admin/sections/PipelinePage"));
const ApplicantsPage = lazy(() => import("./pages/admin/sections/ApplicantsPage"));
const ApplicantDetailPage = lazy(() => import("./pages/admin/sections/ApplicantDetailPage"));
const ApplicationDetailPage = lazy(() => import("./pages/admin/sections/ApplicationDetailPage"));
const MessagesPage = lazy(() => import("./pages/admin/sections/MessagesPage"));
const TeamPage = lazy(() => import("./pages/admin/sections/TeamPage"));
const LeadsPage = lazy(() => import("./pages/admin/sections/LeadsPage"));
const BroadcastPage = lazy(() => import("./pages/admin/sections/BroadcastPage"));
const ActivityPage = lazy(() => import("./pages/admin/sections/ActivityPage"));
const EmailUsagePage = lazy(() => import("./pages/admin/sections/EmailUsagePage"));
const SettingsPage = lazy(() => import("./pages/admin/sections/SettingsPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/sections/AnalyticsPage"));
const AiMonitoringPage = lazy(() => import("./pages/admin/sections/AiMonitoringPage"));
const PendingRemindersPage = lazy(() => import("./pages/admin/sections/PendingRemindersPage"));
const ApiKeysPage = lazy(() => import("./pages/admin/sections/ApiKeysPage"));
const CallsPage = lazy(() => import("./pages/admin/sections/CallsPage"));
const OverviewPage = lazy(() => import("./pages/admin/sections/OverviewPage"));
const MembersPage = lazy(() => import("./pages/admin/sections/MembersPage"));
const PackagesPage = lazy(() => import("./pages/admin/sections/PackagesPage"));
const ServiceRequestsPage = lazy(() => import("./pages/admin/sections/ServiceRequestsPage"));
const PartnersPage = lazy(() => import("./pages/admin/sections/PartnersPage"));
const PartnerDetailPage = lazy(() => import("./pages/admin/sections/PartnerDetailPage"));
const MemberLinksPage = lazy(() => import("./pages/admin/sections/MemberLinksPage"));
const LeadDetailPage = lazy(() => import("./pages/admin/sections/LeadDetailPage"));

// Landing pages (lazy — campaign-specific, not linked from main nav)
const LpFrPrivilege = lazy(() => import("./pages/lp/LpFrPrivilege"));
const LpFrExpatriationVisa = lazy(() => import("./pages/lp/LpFrExpatriationVisa"));
const LpFrRetraite = lazy(() => import("./pages/lp/LpFrRetraite"));
const LpZhSimplified = lazy(() => import("./pages/lp/LpZhSimplified"));
const LpZhTraditional = lazy(() => import("./pages/lp/LpZhTraditional"));
const LpDe = lazy(() => import("./pages/lp/LpDe"));
const LpRu = lazy(() => import("./pages/lp/LpRu"));

const queryClient = new QueryClient();

/**
 * Map of English route names → React elements.
 * Used to build both English and localized routes from the slug map.
 */
const routeComponents: Record<string, React.ReactElement> = {
  memberships: <Memberships />,
  "membership/:tier": <MembershipDetail />,
  about: <About />,
  contact: <Contact />,
  faqs: <FAQs />,
  news: <News />,
  "news/:slug": <ArticleDetail />,
  blog: <Blog />,
  "blog/:slug": <BlogDetail />,
  privileges: <Privileges />,
  visa: <Visa />,
  "thailand-privilege-card": <ThailandPrivilegeCard />,
  "privilege-points": <PrivilegePoints />,
  "how-to-apply": <HowToApply />,
  apply: <Apply />,
  privacy: <Privacy />,
  terms: <Terms />,
  family: <Family />,
  compare: <Compare />,
  guides: <Guides />,
  downloads: <Downloads />,
  "discontinued-memberships": <DiscontinuedMemberships />,
};

const legacyGuideRoutes = [
  { path: "open-bank-account-thailand-elite", guideKey: "bankAccount" },
  { path: "ouverture-de-compte-bancaire", guideKey: "bankAccount" },
  // /what-is-tm30-all-about + /declaration-tm30-thailande removed — TM30 canonical
  // is now /guides/tm30 (and /fr/guides/tm30). The bare slugs 301 to it in the worker.
  { path: "what-is-a-residence-certificate-and-how-to-get-it", guideKey: "residenceCert" },
  { path: "certificat-de-residence-thailande", guideKey: "residenceCert" },
] as const;

const legacyBlogRoutes = [
  "top-golf-courses-bangkok",
  "top-10-best-spas-in-bangkok-2025",
] as const;

/**
 * Build localized route aliases for all languages that have a dedicated domain.
 * These run alongside English routes so that e.g. /adhesions renders <Memberships />.
 * Only routes whose slug differs from the English route name are added.
 */
function buildLocalizedRoutes(lang: string): React.ReactElement[] {
  const routes: React.ReactElement[] = [];
  for (const [routeName, element] of Object.entries(routeComponents)) {
    const localizedSlug = getLocalizedSlug(routeName, lang);
    // Skip if no translation (same as English) to avoid duplicate routes
    if (localizedSlug === routeName) continue;
    routes.push(
      <Route key={`${lang}-${routeName}`} path={localizedSlug} element={element} />
    );
  }
  return routes;
}

/**
 * All application routes defined once.
 * These are rendered both at the root (default language)
 * and under /:lang/* (non-default languages) via LanguageRouter.
 *
 * Localized slug aliases (e.g. /adhesions for FR) are also registered
 * so dedicated-domain deployments serve the correct URL natively.
 */
const appRoutes = (
  <>
    <Route index element={<Index />} />
    {/* English routes */}
    <Route path="memberships" element={<Memberships />} />
    <Route path="membership/:tier" element={<MembershipDetail />} />
    <Route path="about" element={<About />} />
    <Route path="contact" element={<Contact />} />
    <Route path="faqs" element={<FAQs />} />
    <Route path="news" element={<News />} />
    <Route path="news/:slug" element={<ArticleDetail />} />
    <Route path="blog" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogDetail />} />
    <Route path="privileges" element={<Privileges />} />
    <Route path="visa" element={<Visa />} />
    <Route path="thailand-privilege-card" element={<ThailandPrivilegeCard />} />
    <Route path="privilege-points" element={<PrivilegePoints />} />
    <Route path="how-to-apply" element={<HowToApply />} />
    <Route path="member-application-process" element={<HowToApply />} />
    <Route path="devenir-membre" element={<HowToApply />} />
    <Route path="comment-adherer" element={<HowToApply />} />
    <Route path="apply" element={<Apply />} />
    <Route path="application-portal-legacy" element={<ApplyLegacy />} />
    <Route path="privacy" element={<Privacy />} />
    <Route path="terms" element={<Terms />} />
    <Route path="family" element={<Family />} />
    <Route path="compare" element={<Compare />} />
    <Route path="guides" element={<Guides />} />
    <Route path="guides/:slug" element={<GuideDetail />} />
    {legacyGuideRoutes.map((route) => (
      <Route
        key={`legacy-guide-${route.path}`}
        path={route.path}
        element={<GuideDetail guideKeyOverride={route.guideKey} />}
      />
    ))}
    {legacyBlogRoutes.map((slug) => (
      <Route
        key={`legacy-blog-${slug}`}
        path={slug}
        element={<BlogDetail legacySlug={slug} canonicalPathOverride={`/${slug}`} />}
      />
    ))}
    <Route path="downloads" element={<Downloads />} />
    <Route path="discontinued-memberships" element={<DiscontinuedMemberships />} />
    {/* Localized slug aliases — generated from config.slugMap for every language
        that has any localized slug. Without these, /de/mitgliedschaften would 404
        even though the worker hreflang advertises it. */}
    {Object.keys(config.slugMap || {})
      .reduce<string[]>((acc, route) => {
        const langs = Object.keys(config.slugMap?.[route] || {});
        for (const l of langs) if (!acc.includes(l)) acc.push(l);
        return acc;
      }, [])
      .flatMap(lang => buildLocalizedRoutes(lang))}
  </>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PageAlternatesProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <TrackingScripts />
              <TrackingInit />
              <PageViewTracker />
              <CookieConsent />
              <ScrollToTop />
              <RedirectHandler />
              <Suspense fallback={null}>
              <Routes>
            {/* Default language — no prefix */}
            <Route element={<LanguageRouter />}>
              {appRoutes}
            </Route>

            {/* Non-default languages — with /:lang prefix */}
            <Route path=":lang" element={<LanguageRouter />}>
              {appRoutes}
            </Route>

            {/* Portal auth routes (public) */}
            <Route path="portal/auth/callback" element={<AuthCallback />} />
            <Route path="portal/login" element={<Login />} />
            <Route path="portal/register" element={<Register />} />
            <Route path="portal/forgot-password" element={<ForgotPassword />} />
            <Route path="portal/reset-password" element={<ResetPassword />} />
            <Route path="member-link/confirm" element={<MemberLinkConfirm />} />
            {/* One-click survey answers from automation emails (signed token, no login) */}
            <Route path="feedback" element={<Feedback />} />

            {/* Portal protected routes */}
            <Route path="portal" element={<ProtectedRoute />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<PortalDashboard />} />
              <Route path="onboarding" element={<OnboardingSurvey />} />
              <Route path="apply/:id/step/:step" element={<ApplicationWizard />} />
            </Route>

            {/* Partner sign-up (public) — agents register here, never via the client portal */}
            <Route path="partner/register" element={<PartnerRegister />} />

            {/* Partner portal — external partners acting for their own clients */}
            <Route path="partner" element={<PartnerRoute />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="onboarding" element={<PartnerOnboarding />} />
              <Route path="pending" element={<PartnerPending />} />
              <Route path="dashboard" element={<PartnerDashboard />} />
              <Route path="apply/:id/step/:step" element={<ApplicationWizard routeBase="/partner" />} />
            </Route>

            {/* Admin protected routes — obscured path, not indexed */}
            <Route path="admin" element={<AdminRoute />}>
              {/* Legacy redirects — send old URLs to new layout */}
              <Route path="dashboard" element={<Navigate to="/admin/overview" replace />} />
              <Route path="staff"     element={<Navigate to="/admin/overview" replace />} />
              <Route path="preview/:userId" element={<AdminPreview />} />
              {/* New layout — separate pages with persistent sidebar */}
              <Route element={<AdminLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview"               element={<OverviewPage />} />
                <Route path="applications"           element={<ApplicationsPage />} />
                <Route path="pipeline"               element={<PipelinePage />} />
                <Route path="applications/:appId"    element={<ApplicationDetailPage />} />
                <Route path="applicants"             element={<ApplicantsPage />} />
                <Route path="applicants/:userId"     element={<ApplicantDetailPage />} />
                <Route path="messages"               element={<MessagesPage />} />
                <Route path="team"                   element={<TeamPage />} />
                <Route path="leads"                  element={<LeadsPage />} />
                <Route path="leads/:leadId"          element={<LeadDetailPage />} />
                <Route path="broadcast"              element={<BroadcastPage />} />
                <Route path="activity"               element={<ActivityPage />} />
                <Route path="email"                  element={<EmailUsagePage />} />
                <Route path="settings"               element={<SettingsPage />} />
                <Route path="api-keys"               element={<ApiKeysPage />} />
                <Route path="calls"                  element={<CallsPage />} />
                <Route path="analytics"              element={<AnalyticsPage />} />
                <Route path="ai-monitoring"          element={<AiMonitoringPage />} />
                <Route path="reminders"              element={<PendingRemindersPage />} />
                <Route path="members"               element={<MembersPage />} />
                <Route path="packages"              element={<PackagesPage />} />
                <Route path="service-requests"      element={<ServiceRequestsPage />} />
                <Route path="member-links"          element={<MemberLinksPage />} />
                <Route path="partners"              element={<PartnersPage />} />
                <Route path="partners/:partnerId"   element={<PartnerDetailPage />} />
              </Route>
            </Route>

            {/* Landing pages — standalone, outside LanguageRouter */}
            <Route path="lp/fr-thailande-privilege-visa" element={<LpFrPrivilege />} />
            <Route path="lp/fr-thailande-privilege-visa.html" element={<LpFrPrivilege />} />
            <Route path="lp/fr-expatriation-visa-thailande" element={<LpFrExpatriationVisa />} />
            <Route path="lp/fr-expatriation-visa-thailande.html" element={<LpFrExpatriationVisa />} />
            <Route path="lp/fr-retraite-thailande" element={<LpFrRetraite />} />
            <Route path="lp/fr-retraite-thailande.html" element={<LpFrRetraite />} />
            <Route path="lp/de-thailand-elite" element={<LpDe />} />
            <Route path="lp/de-thailand-elite.html" element={<LpDe />} />
            <Route path="lp/ru-thailand-elite" element={<LpRu />} />
            <Route path="lp/ru-thailand-elite.html" element={<LpRu />} />
            <Route path="lp/zh-simplified-thailand-elite" element={<LpZhSimplified />} />
            <Route path="lp/zh-simplified-thailand-elite.html" element={<LpZhSimplified />} />
            <Route path="lp/zh-traditional-thailand-elite" element={<LpZhTraditional />} />
            <Route path="lp/zh-traditional-thailand-elite.html" element={<LpZhTraditional />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </PageAlternatesProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
